import { useSelector } from 'react-redux';
import { FlexContainer, Icon, Separator, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { increasePrice, parsePriceFormat } from 'Containers/Result/components/PaymentLater/utils';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import { FRONT_PLANS } from 'Constants/plans';
import { FlexInfoResponse } from 'Types/common';
import * as Styled from './styles';
import {
  calculateDiscount,
  calculatePercentage,
  NOT_RENDER_INCREASE_DISCOUNT_MAX,
  NOT_RENDER_INCREASE_DISCOUNT_MIN,
} from './utils';

const PayLaterInfoContainer = ({ data }: { data: FlexInfoResponse }): JSX.Element => {
  const { t } = useTranslation(locizeNS.RESULT);
  const paymentLaterProps = useSelector(routeStateSelector);
  const discount = calculateDiscount(data);

  const renderIncreaseDiscount =
    Math.abs(paymentLaterProps?.taxReturnResult) < NOT_RENDER_INCREASE_DISCOUNT_MIN ||
    Math.abs(paymentLaterProps?.taxReturnResult) > NOT_RENDER_INCREASE_DISCOUNT_MAX;

  return (
    <Styled.InfoContainer direction="column" align="center" gap="dimension_16">
      <FlexContainer align="center" gap="dimension_04">
        <img src={`${TAXDOWN_ASSETS_DOMAIN}/plans/smart-filter/v2/live_logo.svg`} alt="live_logo" />
        <Text type="headline" size="S_L_M" color="dark">
          {FRONT_PLANS.FLEX}
        </Text>
      </FlexContainer>
      <Text type="paragraphs" size="M_M_S" color="dark" align="center">
        {t(`plans:PayLater.plan.description`)}
      </Text>
      <Text type="paragraphs" size="M_XL_S" color="dark" align="center">
        {t(`PayLater.result.info`)}
      </Text>
      <FlexContainer direction="column" gap="dimension_08">
        <Styled.BulletContainer>
          <Icon icon="TickXS" color="active" />
          <Text type="paragraphs" color="grey" size="S_M_S">
            {t(`PlanCard.live.Bullet.1`, {
              context: paymentLaterProps?.fromFedUser && 'fed',
            })}
          </Text>
        </Styled.BulletContainer>
        <Styled.BulletContainer>
          <Icon icon="TickXS" color="active" />
          <Text type="paragraphs" color="grey" size="S_M_S">
            {t(`PlanCard.live.Bullet.2`, {
              context: paymentLaterProps?.fromFedUser && 'fed',
            })}
          </Text>
        </Styled.BulletContainer>
      </FlexContainer>
      <Styled.PriceContainer direction="column" fullWidth>
        {data?.price === data?.discountedPrice && data?.price && (
          <FlexContainer justify="space-between" align="center">
            <FlexContainer gap="dimension_04">
              <Text type="paragraphs" size="S_L_S" color="dark" align="center">
                {t(`PayLater.plan`)}
              </Text>
              {!renderIncreaseDiscount && (
                <Text type="paragraphs" size="S_L_S" color="dark" align="center">
                  {t(`PayLater.plan.percentage.info`, {
                    amount: calculatePercentage(
                      Math.abs(data?.price),
                      Math.abs(paymentLaterProps?.taxReturnResult)
                    ),
                  })}
                </Text>
              )}
            </FlexContainer>
            <FlexContainer gap="dimension_10" align="center">
              {renderIncreaseDiscount && (
                <Styled.Price type="paragraphs" size="M_S_S" color="danger_strong" align="center">
                  {increasePrice(Number(parsePriceFormat(data?.price, true)))}
                </Styled.Price>
              )}
              <Text type="paragraphs" size="M_XL_S" color="dark" align="center">
                {parsePriceFormat(data?.price)}
              </Text>
            </FlexContainer>
          </FlexContainer>
        )}
        {data?.price && data?.price !== data?.discountedPrice && (
          <FlexContainer direction="column" gap="dimension_10" fullWidth>
            <FlexContainer justify="space-between">
              <Text type="paragraphs" size="S_S_S" color="dark" align="center">
                {t(`PayLater.plan`)}
              </Text>
              <FlexContainer gap="dimension_10">
                <Styled.Price type="paragraphs" size="M_S_S" color="danger_strong" align="center">
                  {increasePrice(Number(parsePriceFormat(data?.price, true)))}
                </Styled.Price>
                <Text type="paragraphs" size="M_S_S" color="dark" align="center">
                  {parsePriceFormat(data?.price)}
                </Text>
              </FlexContainer>
            </FlexContainer>
            <FlexContainer justify="space-between" fullWidth>
              <Text type="paragraphs" size="S_S_S" color="dark" align="center">
                {t(`PayLater.discount`)}
              </Text>
              <Text type="paragraphs" size="M_S_S" color="dark" align="center">
                {`- ${parsePriceFormat(discount)}`}
              </Text>
            </FlexContainer>
            <Separator />
            <FlexContainer gap="dimension_72" justify="space-between" fullWidth>
              <Text type="paragraphs" size="S_L_S" color="dark" align="center">
                {t(`PayLater.total`)}
              </Text>
              <Text type="paragraphs" size="M_XL_S" color="dark" align="center">
                {parsePriceFormat(data?.discountedPrice)}
              </Text>
            </FlexContainer>
          </FlexContainer>
        )}
      </Styled.PriceContainer>
    </Styled.InfoContainer>
  );
};

export default PayLaterInfoContainer;
